import { Phone, MapPin, ChevronDown, Menu, X, LogIn } from "lucide-react";
import {
  useState,
  useRef,
  useEffect,
  type KeyboardEvent as ReactKeyboardEvent,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import chaLogoWide from "@/assets/cha-logo-wide-name.svg";
// Raw markup of the default name logo: rendered inline so the header never
// depends on a separate image request (Tesla's browser was dropping it and
// showing the text fallback).
import chaLogoWideRaw from "@/assets/cha-logo-wide-name.svg?raw";
import chaLogoIcon from "@/assets/cha-logo-icon.svg";
import NotificationBanner from "@/components/NotificationBanner";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { authLog } from "@/lib/authDebug";
import { useUserRoles } from "@/hooks/use-user-role";
import { formatCommitDate } from "@/lib/utils";
import { AnimatedLogo } from "@/components/AnimatedLogo";
import type { Database } from "@/integrations/supabase/types";
import { programRoute, programMenuLabel } from "@/lib/programPages";

/** A row from the get_public_program_menu() RPC: a visible classroom mapped to a program page. */
interface ProgramMenuItem {
  name: string;
  program_slug: string;
  display_order: number | null;
}

type CalendarRow = Database["public"]["Tables"]["calendars"]["Row"];

/**
 * HEADER LOGO SCHEDULER
 *
 * The header logo and tagline are now managed through the database!
 * Use the Header Logo Scheduler in the admin panel to:
 *
 * 1. Upload different logo SVG files for holidays/seasons
 * 2. Set date ranges for when each logo should appear
 * 3. Customize the tagline for each theme
 *
 * The system automatically selects the appropriate logo based on:
 * - Current date matching scheduled ranges
 * - Priority (specific dates override seasonal ranges)
 * - Falls back to default logo if no schedule matches
 */

const Header = () => {
  const navigate = useNavigate();
  const [programsOpen, setProgramsOpen] = useState(false);
  const [homeMenuOpen, setHomeMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
  const [chafMenuOpen, setChafMenuOpen] = useState(false);
  const [calendars, setCalendars] = useState<CalendarRow[]>([]);
  const [programMenu, setProgramMenu] = useState<ProgramMenuItem[]>([]);
  const [user, setUser] = useState<User | null>(null);
  // Cached, shared role lookup — the header used to fire its own user_roles
  // query on mount AND on every auth event (2+ per app load).
  const { roles: userRoles } = useUserRoles(user?.id);
  const [headerLogo, setHeaderLogo] = useState<string>(chaLogoWide);
  // Markup rendered inline instead of an <img> request; null only for
  // non-SVG uploads. Starts as the bundled logo so first paint never fetches.
  const [headerLogoInline, setHeaderLogoInline] = useState<string | null>(chaLogoWideRaw);
  const [headerTagline, setHeaderTagline] = useState<string>("Nurturing young hearts since 1987");
  const [currentAnimationEffect, setCurrentAnimationEffect] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const homeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const portalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const calendarTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chafTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mobileToggleRef = useRef<HTMLButtonElement | null>(null);

  // Fetch current logo based on date-based schedules
  // Build the Programs menu from visible classrooms mapped to a program page.
  // get_public_program_menu() is added by the staff_programs migration and isn't in
  // the generated types yet — call it through a loose cast; fall back to empty on error.
  useEffect(() => {
    // Call .rpc as a method on the client so `this` stays bound (don't extract it).
    const client = supabase as unknown as {
      rpc: (fn: string) => PromiseLike<{ data: ProgramMenuItem[] | null; error: unknown }>;
    };
    client.rpc("get_public_program_menu").then(({ data, error }) => {
      if (error) {
        console.error("[Header] program menu RPC error:", error);
        return;
      }
      setProgramMenu(data || []);
    });
  }, []);

  // One link per program page: several classrooms can map to the same page
  // (e.g. Preschool A + B), so de-duplicate by program_slug and label by the
  // page's short menu name. Order follows the classrooms' display_order.
  const programLinks = (() => {
    const seen = new Set<string>();
    const links: { slug: string; label: string; route: string }[] = [];
    for (const item of programMenu) {
      if (seen.has(item.program_slug)) continue;
      const route = programRoute(item.program_slug);
      const label = programMenuLabel(item.program_slug);
      if (!route || !label) continue;
      seen.add(item.program_slug);
      links.push({ slug: item.program_slug, label, route });
    }
    return links;
  })();

  useEffect(() => {
    // Storage-hosted logos are ALSO rendered inline when they are SVGs: some
    // networks (the Tesla in-car browser's, notably) kill standalone .svg
    // image requests while allowing everything else, and an <img> that fails
    // degrades to the text fallback. Fetching the markup and inlining it
    // sidesteps the image request entirely; if even the fetch fails, we keep
    // the bundled inline logo rather than ever showing text.
    const applyStorageLogo = async (publicUrl: string) => {
      try {
        const resp = await fetch(publicUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const type = resp.headers.get('content-type') ?? '';
        const looksSvgUrl = publicUrl.split('?')[0].toLowerCase().endsWith('.svg');
        if (type.includes('svg') || looksSvgUrl) {
          const markup = await resp.text();
          // Content-type and extension can both lie when a network
          // intermediary intercepts the request — only inline what is
          // actually SVG markup.
          if (!markup.includes('<svg')) throw new Error('response is not svg markup');
          setHeaderLogo(publicUrl);
          setHeaderLogoInline(markup);
        } else {
          // Non-SVG upload (e.g. PNG) — render via <img> as before.
          setHeaderLogo(publicUrl);
          setHeaderLogoInline(null);
        }
        return true;
      } catch {
        setHeaderLogo(chaLogoWide);
        setHeaderLogoInline(chaLogoWideRaw);
        return false;
      }
    };

    const fetchCurrentLogo = async () => {
      const today = new Date();
      const currentDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format

      // Query for active schedules that match today's date
      const { data: schedules } = await supabase
        .from('header_logo_schedules')
        .select('*')
        .lte('start_date', currentDate)
        .gte('end_date', currentDate)
        .order('is_specific_date', { ascending: false })
        .order('priority', { ascending: false })
        .limit(1);

      if (schedules && schedules.length > 0) {
        // Check if schedule has animation_effect or logo_url
        if (schedules[0].animation_effect) {
          // Use default logo with animation effect
          const { data: settings } = await supabase
            .from('header_logo_settings')
            .select('default_logo_url')
            .maybeSingle();

          if (settings?.default_logo_url) {
            const { data: urlData } = supabase.storage
              .from('header-logos')
              .getPublicUrl(settings.default_logo_url);

            if (urlData) {
              await applyStorageLogo(urlData.publicUrl);
              setCurrentAnimationEffect(schedules[0].animation_effect);
            }
          } else {
            // Use hardcoded default with effect
            setHeaderLogo(chaLogoWide);
            setHeaderLogoInline(chaLogoWideRaw);
            setCurrentAnimationEffect(schedules[0].animation_effect);
          }
          setHeaderTagline(schedules[0].tagline || "Nurturing young hearts since 1987");
        } else if (schedules[0].logo_url) {
          // Use custom SVG without effect
          const { data: urlData } = supabase.storage
            .from('header-logos')
            .getPublicUrl(schedules[0].logo_url);

          if (urlData) {
            await applyStorageLogo(urlData.publicUrl);
            setCurrentAnimationEffect(null);
            setHeaderTagline(schedules[0].tagline || "Nurturing young hearts since 1987");
          }
        }
      } else {
        // Fall back to default from settings
        const { data: settings } = await supabase
          .from('header_logo_settings')
          .select('*')
          .maybeSingle();

        if (settings?.default_logo_url) {
          // Get public URL from storage
          const { data: urlData } = supabase.storage
            .from('header-logos')
            .getPublicUrl(settings.default_logo_url);

          if (urlData) {
            await applyStorageLogo(urlData.publicUrl);
            setCurrentAnimationEffect(null);
            setHeaderTagline(settings.default_tagline || "Nurturing young hearts since 1987");
          }
        } else {
          // Ultimate fallback to the hardcoded default
          setHeaderLogo(chaLogoWide);
          setHeaderLogoInline(chaLogoWideRaw);
          setCurrentAnimationEffect(null);
          setHeaderTagline("Nurturing young hearts since 1987");
        }
      }
    };

    fetchCurrentLogo();
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setProgramsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setProgramsOpen(false);
    }, 200);
  };

  const handlePortalMouseEnter = () => {
    if (portalTimeoutRef.current) {
      clearTimeout(portalTimeoutRef.current);
    }
    setPortalMenuOpen(true);
  };

  const handlePortalMouseLeave = () => {
    portalTimeoutRef.current = setTimeout(() => {
      setPortalMenuOpen(false);
    }, 200);
  };

  const handleHomeMouseEnter = () => {
    if (homeTimeoutRef.current) {
      clearTimeout(homeTimeoutRef.current);
    }
    setHomeMenuOpen(true);
  };

  const handleHomeMouseLeave = () => {
    homeTimeoutRef.current = setTimeout(() => {
      setHomeMenuOpen(false);
    }, 200);
  };

  const handleCalendarMouseEnter = () => {
    if (calendarTimeoutRef.current) {
      clearTimeout(calendarTimeoutRef.current);
    }
    setCalendarMenuOpen(true);
  };

  const handleCalendarMouseLeave = () => {
    calendarTimeoutRef.current = setTimeout(() => {
      setCalendarMenuOpen(false);
    }, 200);
  };

  const handleChafMouseEnter = () => {
    if (chafTimeoutRef.current) {
      clearTimeout(chafTimeoutRef.current);
    }
    setChafMenuOpen(true);
  };

  const handleChafMouseLeave = () => {
    chafTimeoutRef.current = setTimeout(() => {
      setChafMenuOpen(false);
    }, 200);
  };

  const handleDashboardClick = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    authLog('Header.tsx', 'Dashboard click - has session?', { hasSession: !!session });

    if (!session) {
      authLog('Header.tsx', 'No session, navigating to /auth');
      navigate('/auth');
      return;
    }

    // Use cached roles when available; fall back to one fetch if the cache
    // hasn't populated yet (e.g. click immediately after load).
    let effectiveRoles = userRoles;
    if (effectiveRoles.length === 0) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);
      effectiveRoles = roles?.map(r => r.role) || [];
    }
    authLog('Header.tsx', 'Roles resolved', { userRoles: effectiveRoles });

    if (effectiveRoles.includes("admin")) {
      navigate("/admin");
    } else if (effectiveRoles.includes("teacher")) {
      navigate("/teacher-dashboard");
    } else {
      navigate("/parent-dashboard");
    }
    
    setMobileMenuOpen(false);
    setPortalMenuOpen(false);
  };

  // Check authentication status; roles come from the shared useUserRoles hook.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      authLog('Header.tsx', 'Initial getSession', { hasSession: !!session });
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch calendars
  useEffect(() => {
    const fetchCalendars = async () => {
      const { data } = await supabase
        .from('calendars')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (data) {
        // Filter out breakfast and snack calendars, rename lunch to "CHA Menus"
        const filteredCalendars = data
          .filter(calendar => {
            const nameLower = calendar.name.toLowerCase();
            // Exclude breakfast and snack calendars
            return !nameLower.includes('breakfast') && !nameLower.includes('snack');
          })
          .map(calendar => {
            const nameLower = calendar.name.toLowerCase();
            // Rename lunch calendar to "CHA Menus"
            if (nameLower.includes('lunch')) {
              return { ...calendar, name: 'CHA Menus' };
            }
            return calendar;
          });
        
        setCalendars(filteredCalendars);
      }
    };

    fetchCalendars();
  }, []);

  // Keyboard support for the hover dropdowns: Escape closes the menu and
  // returns focus to its trigger; moving focus out of the menu closes it.
  const closeOnEscape =
    (close: () => void) => (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        // Focus the trigger BEFORE closing: its onFocus opens the menu, so
        // closing last leaves the menu closed with focus on the trigger.
        e.currentTarget.querySelector("button")?.focus();
        close();
      }
    };

  const closeOnFocusOut =
    (close: () => void) => (e: ReactFocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        close();
      }
    };

  // Skip-link target: not every page's <main> carries an id, so focus the
  // landmark directly (fall back to the href anchor if none is found).
  const handleSkipToMain = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    const main = document.querySelector("main");
    if (main) {
      e.preventDefault();
      main.setAttribute("tabindex", "-1");
      main.focus({ preventScroll: true });
      main.scrollIntoView();
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuOpen && !(event.target as Element).closest('.mobile-menu-container')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b border-primary-deep shadow-soft">
      <a href="#main-content" className="skip-link" onClick={handleSkipToMain}>
        Skip to main content
      </a>
      <NotificationBanner />
      <div className="w-full">
        {/* Top bar with contact info */}
        <div className="py-4 px-8 text-sm border-b border-primary-foreground/20" style={{ backgroundColor: '#004F87' }}>
          <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
            <div className="flex items-center gap-4 text-primary-foreground/90">
            <a 
              href="tel:5036352139" 
              className="flex items-center gap-1 hover:text-primary-foreground transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span>(503) 635-2139</span>
            </a>
            <a 
              href="https://maps.google.com/?q=17650+SW+65th+Avenue,+Lake+Oswego,+OR+97035" 
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1 hover:text-primary-foreground transition-colors"
            >
              <MapPin className="h-4 w-4" />
              <span>17650 SW 65th Avenue, Lake Oswego, OR</span>
            </a>
          </div>
          </div>
        </div>

        {/* Logo Section with integrated navigation */}
        {/* Band darkened a touch (#2E7DB2 -> #2C78AA) so white nav text meets WCAG AA (4.45:1 -> 4.81:1) */}
        <div className="py-2 sm:py-3 px-4 sm:px-6 lg:px-8 relative" style={{ backgroundColor: '#2C78AA' }}>
          <div className="flex items-center gap-2 sm:gap-3 w-full max-w-7xl mx-auto">
            {/* CHA Logo Icon with white background */}
            <div className="relative bg-white rounded-lg p-1 sm:p-2 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-[#2E7DB2]/20 via-transparent to-[#2E7DB2]/20 rounded-lg"></div>
              <img src={chaLogoIcon} alt="CHA Logo" className="h-12 sm:h-16 md:h-20 lg:h-[6.25rem] w-auto relative z-10" />
            </div>

            {/* School Name, Tagline, and Navigation */}
            <div className="flex flex-col flex-1 min-w-0 -mt-1 sm:-mt-2">
              {/* School Name Logo - responsive sizing */}
              <AnimatedLogo
                src={headerLogo}
                alt="The Children's Hour Academy"
                className="h-14 sm:h-20 md:h-24 lg:h-[7.5rem] w-auto object-contain object-left -mb-4 sm:-mb-6 lg:-mb-8"
                animationEffect={currentAnimationEffect}
                inlineSvg={headerLogoInline ?? undefined}
              />

              {/* Tagline and Desktop Navigation on same line */}
              <div className="flex items-center justify-between gap-2">
                {/* Tagline - left aligned under the T */}
                <p className="text-xs sm:text-sm font-medium text-primary-foreground whitespace-nowrap overflow-hidden text-ellipsis">{headerTagline}</p>

                {/* Desktop Navigation - right justified, ending under the Y */}
                <nav className="hidden lg:flex items-center gap-5 relative z-10">
            <div
              className="relative"
              onMouseEnter={handleHomeMouseEnter}
              onMouseLeave={handleHomeMouseLeave}
              onKeyDown={closeOnEscape(() => setHomeMenuOpen(false))}
              onBlur={closeOnFocusOut(() => setHomeMenuOpen(false))}
            >
              <button
                className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors flex items-center gap-1 py-2"
                aria-haspopup="true"
                aria-expanded={homeMenuOpen}
                onFocus={() => setHomeMenuOpen(true)}
                onClick={() => setHomeMenuOpen(true)}
              >
                Home
                <ChevronDown className={`h-4 w-4 transition-transform ${homeMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              
              {homeMenuOpen && (
                <div className="absolute top-full left-0 pt-0 z-50">
                  <div className="bg-card border border-border rounded-lg shadow-large py-2 min-w-[180px]">
                    <Link 
                      to="/"
                      onClick={() => {
                        window.scrollTo(0, 0);
                        setHomeMenuOpen(false);
                      }}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                    >
                      Home
                    </Link>
                    <Link
                      to="/#about"
                      onClick={() => setHomeMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                    >
                      About
                    </Link>
                    <Link
                      to="/#contact"
                      onClick={() => setHomeMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                    >
                      Contact
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onKeyDown={closeOnEscape(() => setProgramsOpen(false))}
              onBlur={closeOnFocusOut(() => setProgramsOpen(false))}
            >
              <button
                className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors flex items-center gap-1 py-2"
                aria-haspopup="true"
                aria-expanded={programsOpen}
                onFocus={() => setProgramsOpen(true)}
                onClick={() => setProgramsOpen(true)}
              >
                Programs
                <ChevronDown className={`h-4 w-4 transition-transform ${programsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              
              {programsOpen && (
                <div className="absolute top-full left-0 pt-0 z-50">
                  <div className="bg-card border border-border rounded-lg shadow-large py-2 min-w-[220px]">
                    <Link 
                      to="/#programs"
                      onClick={() => setProgramsOpen(false)}
                      className="block px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-accent-soft transition-colors border-b border-border/50"
                    >
                      Our Programs
                    </Link>
                    {programLinks.map((item) => (
                      <Link
                        key={item.slug}
                        to={item.route}
                        onClick={() => setProgramsOpen(false)}
                        className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/meet-our-team" className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors">Meet Our Support Team</Link>
            <Link to="/spark-rating" className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors">SPARK Rating</Link>
            <Link to="/childrens-hour-poem" className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors">The Poem</Link>
            <div
              className="relative"
              onMouseEnter={handleChafMouseEnter}
              onMouseLeave={handleChafMouseLeave}
              onKeyDown={closeOnEscape(() => setChafMenuOpen(false))}
              onBlur={closeOnFocusOut(() => setChafMenuOpen(false))}
            >
              <button
                className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors flex items-center gap-1 py-2"
                aria-haspopup="true"
                aria-expanded={chafMenuOpen}
                onFocus={() => setChafMenuOpen(true)}
                onClick={() => setChafMenuOpen(true)}
              >
                C.H.A.F.
                <ChevronDown className={`h-4 w-4 transition-transform ${chafMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {chafMenuOpen && (
                <div className="absolute top-full left-0 pt-0 z-50">
                  <div className="bg-card border border-border rounded-lg shadow-large py-2 min-w-[180px]">
                    <Link
                      to="/foundation"
                      onClick={() => setChafMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                    >
                      Foundation
                    </Link>
                    <Link
                      to="/foundation/leadership"
                      onClick={() => setChafMenuOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                    >
                      Leadership
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <div
              className="relative"
              onMouseEnter={handleCalendarMouseEnter}
              onMouseLeave={handleCalendarMouseLeave}
              onKeyDown={closeOnEscape(() => setCalendarMenuOpen(false))}
              onBlur={closeOnFocusOut(() => setCalendarMenuOpen(false))}
            >
              <button
                className="text-sm font-medium text-primary-foreground hover:underline underline-offset-4 transition-colors flex items-center gap-1 py-2"
                aria-haspopup="true"
                aria-expanded={calendarMenuOpen}
                onFocus={() => setCalendarMenuOpen(true)}
                onClick={() => setCalendarMenuOpen(true)}
              >
                Calendar
                <ChevronDown className={`h-4 w-4 transition-transform ${calendarMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              
              {calendarMenuOpen && (
                <div className="absolute top-full right-0 pt-0 z-50">
                  <div className="bg-card border border-border rounded-lg shadow-large py-2 min-w-[200px]">
                    {calendars.length > 0 ? (
                      calendars.map((calendar) => (
                        <Link
                          key={calendar.id}
                          to={`/calendar/${calendar.id}`}
                          onClick={() => setCalendarMenuOpen(false)}
                          className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                        >
                          {calendar.name}
                        </Link>
                      ))
                    ) : (
                      <div className="px-4 py-2.5 text-sm text-muted-foreground">
                        No calendars available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div
              className="relative"
              onMouseEnter={handlePortalMouseEnter}
              onMouseLeave={handlePortalMouseLeave}
              onKeyDown={closeOnEscape(() => setPortalMenuOpen(false))}
              onBlur={closeOnFocusOut(() => setPortalMenuOpen(false))}
            >
              <button
                className="text-primary-foreground hover:text-primary-foreground/80 transition-colors p-2"
                aria-label="Portal menu"
                aria-haspopup="true"
                aria-expanded={portalMenuOpen}
                onFocus={() => setPortalMenuOpen(true)}
                onClick={() => setPortalMenuOpen(true)}
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
              
              {portalMenuOpen && (
                <div className="absolute top-full right-0 pt-0 z-50">
                  <div className="bg-card border border-border rounded-lg shadow-large py-2 min-w-[180px]">
                    {!user ? (
                      <button 
                        onClick={handleDashboardClick}
                        className="block w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors flex items-center gap-2"
                      >
                        <LogIn className="h-4 w-4" />
                        Portal Sign-In
                      </button>
                    ) : (
                      <>
                        {(userRoles.includes("boss") || userRoles.includes("dev")) && (
                          <Link
                            to="/boss"
                            onClick={() => setPortalMenuOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                          >
                            Boss Dashboard
                          </Link>
                        )}
                        {userRoles.includes("admin") && (
                          <Link
                            to="/admin"
                            onClick={() => setPortalMenuOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                          >
                            Admin Dashboard
                          </Link>
                        )}
                        {userRoles.includes("teacher") && (
                          <Link
                            to="/teacher-dashboard"
                            onClick={() => setPortalMenuOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                          >
                            Teacher Dashboard
                          </Link>
                        )}
                        {userRoles.includes("parent") && (
                          <Link
                            to="/parent-dashboard"
                            onClick={() => setPortalMenuOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                          >
                            Parent Dashboard
                          </Link>
                        )}
                        {(userRoles.includes("admin") || userRoles.includes("chaf_admin")) && (
                          <Link
                            to="/admin/aid-applications"
                            onClick={() => setPortalMenuOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground hover:bg-accent-soft transition-colors"
                          >
                            CHAF Dashboard
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </nav>

                {/* Mobile Menu Button - right aligned on same line as tagline */}
                <button
                  ref={mobileToggleRef}
                  className="lg:hidden flex-shrink-0 text-primary-foreground p-2 bg-primary/20 rounded-lg hover:bg-primary/30 transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-haspopup="true"
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div
            className="mobile-menu-container lg:hidden absolute top-full left-0 right-0 bg-card border-b border-border shadow-large z-50 max-h-[70vh] overflow-y-auto"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setMobileMenuOpen(false);
                mobileToggleRef.current?.focus();
              }
            }}
          >
            <nav className="py-4 px-4 space-y-2 pb-6">
              <div className="border-b border-border pb-2 mb-2">
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Home</p>
                <Link 
                  to="/"
                  onClick={() => {
                    window.scrollTo(0, 0);
                    setMobileMenuOpen(false);
                  }}
                  className="block py-2.5 px-4 text-sm text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  Home
                </Link>
                <Link 
                  to="/#about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  About
                </Link>
                <Link 
                  to="/#contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  Contact
                </Link>
              </div>
              
              <div className="border-t border-border pt-2 mt-2">
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Programs</p>
                <Link
                  to="/#programs"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  Our Programs
                </Link>
                {programLinks.map((item) => (
                  <Link
                    key={item.slug}
                    to={item.route}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2.5 px-4 text-sm text-foreground hover:bg-accent-soft transition-colors rounded"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              
              <div className="border-t border-border pt-2 mt-2">
                <Link 
                  to="/meet-our-team"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  Meet Our Support Team
                </Link>
                <Link 
                  to="/spark-rating"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  SPARK Rating
                </Link>
                <Link 
                  to="/childrens-hour-poem"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  The Children's Hour Poem
                </Link>
                <Link
                  to="/foundation"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  Foundation
                </Link>
                <Link
                  to="/foundation/leadership"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2.5 px-8 text-sm text-foreground hover:bg-accent-soft transition-colors rounded"
                >
                  Leadership
                </Link>
              </div>
              
              <div className="border-t border-border pt-2 mt-2">
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Calendar</p>
                {calendars.length > 0 ? (
                  calendars.map((calendar) => (
                    <Link
                      key={calendar.id}
                      to={`/calendar/${calendar.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                    >
                      {calendar.name}
                    </Link>
                  ))
                ) : (
                  <div className="px-4 py-2.5 text-sm text-muted-foreground">
                    No calendars available
                  </div>
                )}
              </div>
              
              <div className="border-t border-border pt-2 mt-2">
                <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Portal Access</p>
                {!user ? (
                  <button 
                    onClick={handleDashboardClick}
                    className="block w-full text-left py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded flex items-center gap-2"
                  >
                    <LogIn className="h-4 w-4" />
                    Portal Sign-In
                  </button>
                ) : (
                  <>
                    {(userRoles.includes("boss") || userRoles.includes("dev")) && (
                      <Link
                        to="/boss"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                      >
                        Boss Dashboard
                      </Link>
                    )}
                    {userRoles.includes("admin") && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    {userRoles.includes("teacher") && (
                      <Link
                        to="/teacher-dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                      >
                        Teacher Dashboard
                      </Link>
                    )}
                    {userRoles.includes("parent") && (
                      <Link
                        to="/parent-dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                      >
                        Parent Dashboard
                      </Link>
                    )}
                    {(userRoles.includes("admin") || userRoles.includes("chaf_admin")) && (
                      <Link
                        to="/admin/aid-applications"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block py-2.5 px-4 text-sm font-medium text-foreground hover:bg-accent-soft transition-colors rounded"
                      >
                        CHAF Dashboard
                      </Link>
                    )}
                  </>
                )}
              </div>
              
              {/* Commit Info - Only visible to admin and teacher roles */}
              {(userRoles.includes("admin") || userRoles.includes("teacher") || userRoles.includes("support")) && (
                <div className="border-t border-border pt-2 mt-2">
                  <div className="px-4 py-3 text-[10px] text-muted-foreground bg-sidebar-accent/10 rounded">
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Commit:</span>
                        <span className="font-mono">{import.meta.env.VITE_GIT_COMMIT_HASH}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Date:</span>
                        <span>{formatCommitDate(import.meta.env.VITE_GIT_COMMIT_DATE)}</span>
                      </div>
                      <div title={import.meta.env.VITE_GIT_COMMIT_MESSAGE} className="truncate mt-1 opacity-75">
                        {import.meta.env.VITE_GIT_COMMIT_MESSAGE}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Extra spacing for Safari bottom navigation bar on iOS */}
              <div className="h-20" aria-hidden="true" />
            </nav>
          </div>
        )}
       </div>
    </header>
  );
};

export default Header;
